import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import { animateReorder, replayAnimation } from "../lib/motion"

// Chat List Update Controller
// Real-time updates for the /chats index page via ActionCable.
//
// When a new message arrives in any of the user's chats:
// 1. Updates last message preview text
// 2. Updates timestamp
// 3. Updates/shows unread badge count
// 4. Moves the chat card to the top of the list (FLIP-animated; an incoming message also flashes the card)
//
// Payload from ChatListChannel:
//   { chat_id, last_message, is_own_message, is_image, timestamp_text, unread_count }
//
// Usage: data-controller="chat-list-update" on #chat_list section
export default class extends Controller {
  connect() {
    this.subscription = consumer.subscriptions.create("ChatListChannel", {
      connected: () => {
        console.debug("[ChatListUpdate] Connected")
      },

      disconnected: () => {
        console.debug("[ChatListUpdate] Disconnected")
      },

      received: (data) => {
        this.handleUpdate(data)
      }
    })
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleUpdate(data) {
    const { chat_id, last_message, is_own_message, is_image, timestamp_text, unread_count } = data
    const card = document.getElementById(`chat_${chat_id}`)

    if (!card) {
      console.debug(`[ChatListUpdate] Card not found: chat_${chat_id}`)
      return
    }

    // 1. Update last message preview
    const messageEl = card.querySelector("[data-chat-last-message]")
    if (messageEl) {
      const prefix = is_own_message ? `${this.mePrefix} ` : ""
      messageEl.textContent = prefix + (last_message || "")
      // Reset to normal style (remove italic "no messages yet" style)
      messageEl.className = "text-sm text-charcoal-600 truncate"
    }

    // 2. Update timestamp
    const timestampEl = card.querySelector("[data-chat-timestamp]")
    if (timestampEl) {
      timestampEl.textContent = timestamp_text || ""
    }

    // 3. Update unread badge
    const badgeEl = card.querySelector("[data-chat-unread-badge]")
    if (badgeEl) {
      if (unread_count > 0) {
        const display = unread_count > 99 ? "99+" : unread_count
        badgeEl.textContent = display
        badgeEl.className = "inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-crust-100 text-crust-800"
        replayAnimation(badgeEl, "badge-pop")
      } else {
        badgeEl.textContent = ""
        badgeEl.className = "hidden"
      }
    }

    // 4. Move card to top of list (most recent chat first)
    if (this.element.firstElementChild !== card) {
      animateReorder(this.element, () => this.element.insertBefore(card, this.element.firstElementChild))
    }
    const surface = card.querySelector('[data-chat-swipe-target="card"]')
    if (surface && !is_own_message) replayAnimation(surface, "chat-card-flash")

    console.debug(`[ChatListUpdate] Updated chat_${chat_id}: "${last_message?.substring(0, 30)}"`)
  }

  // Lazy-loaded "you:" prefix from body data attribute (set by layout)
  get mePrefix() {
    if (!this._mePrefix) {
      this._mePrefix = document.body.dataset.chatMePrefix || "你："
    }
    return this._mePrefix
  }
}
